import type { ColorRamp, M3RoleTokens, SemanticColor, ThemeTokens } from "../theme/types";

// M3 role tokens — fixed swatches (light theme's set). This is now the
// source of the brand color (accentRamp below is retinted to derive from
// `primary`) — update this object first if the brand color ever changes
// again, then regenerate the ramp the same way (see the ramp's own comment
// for the derivation method). Renamed from `m3Roles` now that dark mode has
// its own real values (`darkM3Roles` below) instead of reusing this one —
// no longer "same hex in both themes" like accentRamp/neutralRamp still are.
export const lightM3Roles: M3RoleTokens = {
  primary: "#0E6B5C",
  onPrimary: "#FFFFFF",
  primaryContainer: "#B7F0E1",
  onPrimaryContainer: "#00201A",

  secondary: "#4A635E",
  onSecondary: "#FFFFFF",
  secondaryContainer: "#CCE8E0",
  onSecondaryContainer: "#051F1B",

  // Plum/violet — deliberately NOT reused for loan/transaction status.
  // Was gold/amber (#7A5900) originally, which sat too close in hue
  // (~42-44°) to the `warning` semantic color below (#B48A2E/#8A6A1F, also
  // gold/amber, ~41-42°) — differentiated only by saturation/lightness, not
  // hue, so a tertiary-colored element next to a warning badge would have
  // read as "same color, different shade." Shifted to plum/violet (~280°)
  // instead, ~239° away in hue from warning — unambiguously distinct now.
  // WCAG AA re-verified: on-tertiary/tertiary 7.1:1, on-container/container
  // 13.0:1 (both well past the 4.5:1 baseline).
  tertiary: "#6B4C7A",
  onTertiary: "#FFFFFF",
  tertiaryContainer: "#EAD9F5",
  onTertiaryContainer: "#241429",

  error: "#B3261E",
  onError: "#FFFFFF",
  errorContainer: "#F9DEDC",
  onErrorContainer: "#410E0B",

  surfaceDim: "#DAD9D4",
  surface: "#FAFAF8",
  surfaceBright: "#FFFFFF",
  onSurface: "#1A1C1A",
  onSurfaceVariant: "#414942",
  outline: "#717972",
  outlineVariant: "#C1C9C1",

  inverseSurface: "#2E312E",
  inverseOnSurface: "#EFF1EC",
  inversePrimary: "#8AD5C1",
};

// Dark theme's M3 role tokens — real dark-tone values, not a reuse of
// lightM3Roles. Follows M3's own light/dark tone-inversion pattern: roles
// generally use a *lighter* tone in dark mode (readable against a dark
// surface), containers use a *darker* tone, "on" colors flip accordingly.
// All 8 role/on-role and container/on-container pairs re-verified at
// WCAG AA — every pair clears 7.2:1+ (well past the 4.5:1 baseline), see
// TOKENS.md for the full table.
export const darkM3Roles: M3RoleTokens = {
  primary: "#8AD5C1",
  onPrimary: "#00382E",
  primaryContainer: "#005043",
  onPrimaryContainer: "#B7F0E1",

  secondary: "#B0CCC4",
  onSecondary: "#1B3530",
  secondaryContainer: "#324B46",
  onSecondaryContainer: "#CCE8E0",

  tertiary: "#D7BCE0",
  onTertiary: "#3B2545",
  tertiaryContainer: "#52355D",
  onTertiaryContainer: "#EAD9F5",

  error: "#FFB4AB",
  onError: "#690005",
  errorContainer: "#93000A",
  onErrorContainer: "#FFDAD6",

  surfaceDim: "#101211",
  surface: "#1A1C1A",
  surfaceBright: "#363937",
  onSurface: "#E2E3DE",
  onSurfaceVariant: "#C1C9C1",
  outline: "#8B938C",
  outlineVariant: "#414942",

  inverseSurface: "#E2E3DE",
  inverseOnSurface: "#2E312E",
  inversePrimary: "#0E6B5C",
};

// Fixed swatches — same hex in both themes. Retinted from the old brand
// green (#1a7a4a) to derive from lightM3Roles.primary (#0E6B5C) above: each
// step is generated by solving the old ramp's white/black-mix ratio relative
// to old accent-600 (e.g. old accent-50 = old-600 mixed 91% toward white) and
// reapplying that same mix ratio to the new primary — preserves the ramp's
// relative lightness/usability steps (and its WCAG contrast behavior)
// without hand-picking new values. accent-600 is pinned exactly to
// lightM3Roles.primary. accent-500 re-verified: white text still clears WCAG AA
// 4.5:1 (6.4:1 now, more headroom than the old ramp's ~4.5:1) — see
// TOKENS.md for the derivation script.
export const accentRamp: ColorRamp = {
  50: "#F2F7F6",
  100: "#EDF4F3",
  200: "#CADEDB",
  300: "#A0C5BF",
  400: "#72A89F",
  500: "#1A7364",
  600: "#0E6B5C",
  700: "#0B574B",
  800: "#094238",
  900: "#062D27",
};

export const neutralRamp: ColorRamp = {
  50: "#F9FAFB",
  100: "#F3F4F6",
  200: "#E5E7EB",
  300: "#D1D5DB",
  400: "#9CA3AF",
  500: "#6B7280",
  600: "#4B5563",
  700: "#374151",
  800: "#1F2937",
  900: "#111827",
};

// Muted, desaturated semantic colors — deliberately calmer than default
// Tailwind red/green so the lending-product UI doesn't read as alarming.
// success is hue-shifted to olive/sage (was the same green family as accent,
// ~152°) specifically so "this succeeded" (a status badge) stays visually
// distinct from "this is clickable" (the accent color) now that accent is
// also green — same muted/desaturated treatment, different hue family.
// success.solid is also corrected here: the old #3F8F6B only hit 3.92:1
// against white (fails the 4.5:1 AA baseline this file's semantics are
// supposed to meet) — unused by any component today (Badge only reads
// bg/fg/border), but fixed while this token is already being touched rather
// than carried forward broken.
const lightSemantics: Record<"success" | "warning" | "error" | "info", SemanticColor> = {
  success: { bg: "#F7FBF0", border: "#D7E5BF", fg: "#516E1F", solid: "#617E30", onSolid: "#FFFFFF" },
  warning: { bg: "#FBF6EC", border: "#E5D3A6", fg: "#8A6A1F", solid: "#B48A2E", onSolid: "#FFFFFF" },
  error: { bg: "#FBEEEC", border: "#E5B8AF", fg: "#963B2B", solid: "#B8483A", onSolid: "#FFFFFF" },
  info: { bg: "#EEF3FB", border: "#B8CBE5", fg: "#2B5896", solid: "#3A6EB8", onSolid: "#FFFFFF" },
};

const darkSemantics: Record<"success" | "warning" | "error" | "info", SemanticColor> = {
  success: { bg: "#212913", border: "#3D4E1F", fg: "#AFCB7F", solid: "#617E30", onSolid: "#0B0E0D" },
  warning: { bg: "#2A2213", border: "#4E3F1F", fg: "#D8B770", solid: "#B48A2E", onSolid: "#0B0E0D" },
  error: { bg: "#2A1613", border: "#4E241F", fg: "#E08776", solid: "#B8483A", onSolid: "#0B0E0D" },
  info: { bg: "#131F2A", border: "#1F344E", fg: "#83AEDD", solid: "#3A6EB8", onSolid: "#0B0E0D" },
};

export const lightTheme: ThemeTokens = {
  background: "#FAFAF8",
  surface: "#F7F7F4",
  surfaceRaised: "#FFFFFF",
  border: neutralRamp[200],
  text: neutralRamp[900],
  textMuted: neutralRamp[500],
  textSubtle: neutralRamp[400],
  accent: accentRamp,
  neutral: neutralRamp,
  onAccent: "#FFFFFF",
  m3: lightM3Roles,
  ...lightSemantics,
};

export const darkTheme: ThemeTokens = {
  background: "#0B0E0D",
  surface: "#121613",
  surfaceRaised: "#171C19",
  border: neutralRamp[700],
  text: neutralRamp[50],
  textMuted: neutralRamp[400],
  textSubtle: neutralRamp[600],
  accent: accentRamp,
  neutral: neutralRamp,
  onAccent: "#FFFFFF",
  m3: darkM3Roles,
  ...darkSemantics,
};
